import { useMemo, useState } from 'react';
import { sanitizeExternalUrl } from '../lib/urls';
import { photoObjectPosition } from '../lib/members';

const FALLBACK_IMAGE = 'https://placehold.co/360x144?text=Event+Preview';

export default function EventCard({ event, attendeeMembers = [], fullWidth = false }) {
  const eventUrl = sanitizeExternalUrl(event?.url, 'event');

  const initialImage = useMemo(() => event?.imageUrl || event?.image_url || FALLBACK_IMAGE, [event]);
  const [imgSrc, setImgSrc] = useState(initialImage);

  const [attendeeExpanded, setAttendeeExpanded] = useState(false);

  const attendees = Array.isArray(attendeeMembers) ? attendeeMembers : [];
  const visible = attendees.slice(0, 5);
  const extraCount = Math.max(0, attendees.length - visible.length);

  return (
    <div className={`bg-white shadow-lg rounded-lg overflow-hidden min-w-[280px] w-full transition-all duration-700 ease-in-out opacity-100 scale-100 motion-reduce:transition-none ${fullWidth ? 'max-w-none' : 'max-w-[360px]'}`}>
      <div className="relative h-36">
        <img
          src={imgSrc}
          alt={event.title}
          className="w-full h-full object-cover"
          loading="lazy"
          referrerPolicy="no-referrer"
          onError={() => {
            if (imgSrc !== FALLBACK_IMAGE) setImgSrc(FALLBACK_IMAGE);
          }}
        />
        <div className="absolute inset-0 bg-black/40 flex items-center justify-center p-4">
          <span className="text-white text-lg font-playfair-serif text-center break-words">
            {event.title}
          </span>
        </div>
      </div>
      <div className="p-4">
        <div className="mb-3">
          <p className="text-sm text-gray-600">
            {new Date(event.date).toLocaleDateString('en-CA', {
              weekday: 'long',
              year: 'numeric',
              month: 'long',
              day: 'numeric',
              hour: 'numeric',
              minute: 'numeric',
            })}
          </p>
          <p className="text-sm text-gray-600">{event.location}</p>
        </div>
        <p className="text-sm text-gray-700 mb-4">{event.description}</p>
        <div className="flex justify-between items-center gap-3">
          <div className="flex items-center gap-2">
            <span className={`px-2 py-1 text-xs rounded-full ${
              event.type === 'NBNA' 
                ? 'bg-NBNA-primary/10 text-NBNA-primary'
                : event.type === 'Executive'
                ? 'bg-purple-100 text-purple-700'
                : 'bg-gray-100 text-gray-700'
            }`}>
              {event.type}
            </span>

            {attendees.length ? (
              <button
                type="button"
                className="flex items-center"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setAttendeeExpanded((prev) => !prev);
                }}
                aria-label="Show attendees"
              >
                <div className="flex -space-x-2">
                  {visible.map((m) => (
                    <div
                      key={m.id}
                      className="h-6 w-6 rounded-full border border-white bg-gray-200 overflow-hidden"
                      title={m.name || m.businessName || 'Member'}
                    >
                      {m.photo ? (
                        <img src={m.photo} alt={m.name || 'Member'} style={{ objectPosition: photoObjectPosition(m) }} className="h-full w-full object-cover" />
                      ) : (
                        <div className="h-full w-full flex items-center justify-center text-[10px] text-gray-700">
                          {String(m.name || m.businessName || 'M').trim().slice(0, 1).toUpperCase()}
                        </div>
                      )}
                    </div>
                  ))}
                  {extraCount ? (
                    <div className="h-6 w-6 rounded-full border border-white bg-gray-100 flex items-center justify-center text-[10px] text-gray-700">
                      +{extraCount}
                    </div>
                  ) : null}
                </div>
              </button>
            ) : null}
          </div>

          {eventUrl ? (
            <a
              href={eventUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="text-NBNA-primary hover:text-NBNA-primary/70 transition-colors text-sm"
            >
              View Details →
            </a>
          ) : null}
        </div>

        {attendeeExpanded && attendees.length ? (
          <div className="mt-3 border-t pt-3">
            <div className="text-xs font-medium text-gray-700">Attending</div>
            <ul className="mt-2 space-y-2">
              {attendees.map((m) => (
                <li key={m.id} className="flex items-center gap-2">
                  <div className="h-7 w-7 rounded-full bg-gray-200 overflow-hidden">
                    {m.photo ? (
                      <img src={m.photo} alt={m.name || 'Member'} style={{ objectPosition: photoObjectPosition(m) }} className="h-full w-full object-cover" />
                    ) : (
                      <div className="h-full w-full flex items-center justify-center text-xs text-gray-700">
                        {String(m.name || m.businessName || 'M').trim().slice(0, 1).toUpperCase()}
                      </div>
                    )}
                  </div>
                  <div className="text-sm text-gray-800">{m.name || m.businessName || 'Member'}</div>
                </li>
              ))}
            </ul>
          </div>
        ) : null}
      </div>
    </div>
  );
}
